/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import {load, register} from '@shopify/theme-sections';
import * as product from '@shopify/theme-product';
import {ProductForm , getUrlWithVariant} from '@shopify/theme-product-form';
import {formatMoney} from '@shopify/theme-currency';
import Swiper, { Navigation, Thumbs, A11y, Keyboard, FreeMode, Pagination } from 'swiper';
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
// import Drift from 'drift-zoom';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',
    originalSelectorId: '[data-product-select]',
    priceWrapper: '[data-price-wrapper]',
    productImageWrapper: '[data-product-image-wrapper]',
    productFeaturedImage: '[data-product-featured-image]',
    productJson: '[data-product-page]',
    productPrice: '[data-product-price]',
    productThumbnailsSlider: 'carousel--product-thumbnails',
    productFeatureSlider: 'carousel--featured-product',
    productSlides: '.swiper-wrapper .swiper-slide',
    productThumbs: '[data-product-single-thumbnail]',
    productForm: '[data-product-form]',
    radioOptionSelector: '[data-radio-option-selector]',
    singleOptionSelector: '[data-single-option-selector]',
    productMediaWrapper: '[data-product-single-media-wrapper]',
    productMediaTypeVideo: '[data-product-media-type-video]',
    productMediaTypeModel: '[data-product-media-type-model]',
    variantOptionLabels: '.swatch__option label',
    formIncrement: '.form__increment',
    quantity: '#Quantity',
    orderSheet: '#order-sheet',
    productCTA: '.product__cta',
    productSwiperThumbs: null,
    productSwiperFeature: null
};

const cssClasses = {
    activeThumbnail: 'active-thumbnail',
    hide: 'hide',
};

document.addEventListener('DOMContentLoaded', () => {
    /**
     * Product section constructor. Runs on page load as well as Theme Editor
     * `section:load` events.
     * @param {string} container - selector for the section container DOM element
    */
    register('product', {
        async onLoad() {
            const productFormElement = document.querySelector(selectors.productForm);
            const productHandle      = productFormElement.dataset.productHandle;
            const selectedVariantId  = productFormElement.dataset.onloadVariantId;

            // Stop parsing if we don't have the product json script tag when loading
            // section in the Theme Editor
            if (!this.container.querySelector(selectors.productJson)?.innerHTML) {
                return;
            }

            await fetch(`${Shopify.routes.root}products/${productHandle}.js`)
            .then(response => {
                return response.json();
            })

            .then(productJSON => {
                this.product = productJSON;
                this.productForm = new ProductForm(productFormElement, this.product, {
                    onOptionChange: this.onFormOptionChange.bind(this),
                    onQuantityChange: this.onFormQuantityChange.bind(this)
                });
            });

            this.currentVariant = this.productForm.product.variants.filter(variant => variant.id == selectedVariantId)[0];
            this.featuredImage = document.querySelector(selectors.productFeaturedImage);
            this.swiperSlides = this.featuredImage.querySelectorAll(selectors.productSlides);

            // Add GSAP Scroll Effect to Order CTA
            this.productCTA = productFormElement.querySelector(selectors.productCTA);
            this.orderSheet = document.querySelector(selectors.orderSheet);
            if (this.orderSheet) {
                gsap.registerPlugin(ScrollToPlugin);

                this.productCTA.addEventListener('click', () => {
                    gsap.to(window, {
                        duration: .2,
                        scrollTo: { y: selectors.orderSheet, offsetY: 50 },
                        ease: "power2",
                    });
                });
            }

            this.initProductCarousel();
            this.initProductVideo();
            this.initVariantTitles();
            this.initVariantHoverWatch();
            this.initStickyCart();
            // this.initProductZoom();

    window.addEventListener('load', () => {
        const urlParams = new URLSearchParams(window.location.search);
        const variantIdFromURL = urlParams.get('variant');

        if (!variantIdFromURL) return;

        // Wait for product JSON to be available
        const checkVariantReady = () => {
            // Assume this.product.variants is an array of variant objects
            const matchingVariant = this.product?.variants?.find(v => v.id == variantIdFromURL);

            if (matchingVariant) {
            // Construct mock event similar to onFormOptionChange usage
            const mockEvent = {
                dataset: {
                variant: matchingVariant
                }
            };

            // Reuse your existing method
            this.onFormOptionChange(mockEvent);
            } else {
            // Try again next frame if not ready yet
            requestAnimationFrame(checkVariantReady);
            }
        };

        checkVariantReady();
        });

        },

        onFormOptionChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            if (currentVariant.variant) {
                this.currentVariant = currentVariant.variant;
            }

            const variantChange = this.updateMediaGallery(currentVariant);
            this.updatePDPInformation(currentVariant);
            this.updateProductPrices(currentVariant);
            this.updateURLwithVariant(currentVariant)
            this.updateVariantSKU(currentVariant);

            if (variantChange == false && this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariant);
            }
        },

        onFormQuantityChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            this.updateProductPrices(currentVariant);
        },

        initVariantTitles() {
            this.changeVariantTitles(this.currentVariant);
        },

        updateURLwithVariant(event) {
            const variant = event.variant;

            if (!variant) return;

            const url = getUrlWithVariant(window.location.href, variant.id);
            window.history.replaceState({ path: url }, '', url);
        },

        updateVariantSKU(event) {
            const variant = event.variant;
            const skuElement = this.container.querySelector('.product__sku-value');

            if (variant && skuElement) {
                skuElement.textContent = variant.sku || 'N/A';
            }
        },

        updateMediaGallery(event) {
            const variant = event.variant;
            const variantId = variant?.id;

            // Don't update if only main product gallery exists
            if (document.querySelectorAll('.product__images-temp').length == 1) {
                return false;
            }

            const featuredGalleryWrapper = document.querySelector('.carousel--featured-product .swiper-wrapper');

            // Load main gallery if variantId is 'main'
            if (variantId === 'main') {
                const mainMedia = document.querySelector(`.product__images-temp[data-variant-id="main"]`);
                if (mainMedia) {
                    featuredGalleryWrapper.innerHTML = mainMedia.innerHTML;
                }
            } else {
                // Load variant gallery if exists
                const tempMedia = document.querySelector(`.product__images-temp[data-variant-id="${variantId}"]`);
                const mainMedia = document.querySelector(`.product__images-temp[data-variant-id="main"]`);

                if (tempMedia) {
                    featuredGalleryWrapper.innerHTML = tempMedia.innerHTML;
                } else if (mainMedia) {
                    featuredGalleryWrapper.innerHTML = mainMedia.innerHTML;
                }
            }

            // Destroy and re-initialize Swiper with new assets
            selectors.productSwiperFeature.destroy(true, true);
            this.initProductCarousel();
            return true;
        },

        updatePDPInformation(event) {
            this.updateMasterSelect(event);
           // this.updateAddToCartState(event);
            this.changeVariantTitles(event);
        },

        updateMasterSelect(event) {
            const variant = event.variant;

            if (!variant) return;

            this.container.querySelector(selectors.originalSelectorId).value = variant.id;
        },

        updateAddToCartState(event) {
            const variant = event.variant;

            if (variant) {
                this.container.querySelector(selectors.priceWrapper).classList.remove(cssClasses.hide);
            } else {
                this.container.querySelector(selectors.addToCart).disabled = true;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.unavailable;
                this.container.querySelector(selectors.priceWrapper).classList.add(cssClasses.hide);
                return;
            }

            if (variant.available) {
                this.container.querySelector(selectors.addToCart).disabled = false;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.addToCart;
            } else {
                this.container.querySelector(selectors.addToCart).disabled = true;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.soldOut;
            }
        },

        changeVariantTitles() {
            document.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach((swatch__option) => {
                const container = swatch__option.closest('.form__element');
                const subtitle = container.querySelector('.swatch__title .swatch__subtitle');
                subtitle.textContent = swatch__option.value;
            });
        },

        tempChangeVariantTitle(event) {
            const target = event.currentTarget;
            const newLabel = target.textContent;
            const container = target.closest('.form__element');
            const subtitle = container.querySelector('.swatch__title .swatch__subtitle');
            subtitle.textContent = newLabel;
        },

        initVariantHoverWatch() {
            const changeVariantTitles = this.changeVariantTitles;
            const tempChangeVariantTitle = this.tempChangeVariantTitle;
            document.querySelectorAll('.swatch__option input[data-single-option-selector] + label.form__option-label').forEach((swatch__option) => {
                swatch__option.addEventListener('mouseenter', tempChangeVariantTitle);
                swatch__option.addEventListener('mouseleave', changeVariantTitles);
            });
        },

        updateImages(event) {
            const variant = event.variant;
            if (variant != null && typeof variant.featured_media != 'undefined') {
                const imageId = this.id + '-' + variant.featured_media.id;
                this.swapImage(imageId);
            }
        },

        updateProductPrices(evt) {
            const variant = evt.variant;
            if (!variant) {
                return false;
            }
            const comparePrice = this.container.querySelector(selectors.comparePrice);
            const comparePriceText = this.container.querySelector(selectors.comparePriceText);
            // const quantity = parseInt(this.container.querySelector(selectors.quantity).value);

            this.container.querySelector(selectors.productPrice).innerHTML = formatMoney(variant.price, theme.settingCurrencyFormat);

            // const priceWithQuantity = variant.price * quantity;
            // document.querySelector('#addToCartPricing').innerHTML = formatMoney(priceWithQuantity, theme.moneyWithoutCurrency);

            if (variant.compare_at_price > variant.price) {
                comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.settingCurrencyFormat);
                comparePrice.classList.remove(cssClasses.hide);
                comparePriceText.classList.remove(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.add('price__original--sale');
            } else if(comparePrice) {
                comparePrice.innerHTML = '';
                comparePrice.classList.add(cssClasses.hide);
                comparePriceText.classList.add(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.remove('price__original--sale');
            }
        },

        disableVariants(target, valuesToEnable) {
            // Loop through all the Option Selectors and check which one is a swatch selector
            // The target is usually the main product container
            const optionSelector1 = target.querySelectorAll('[data-index="option1"]');
            const optionSelector2 = target.querySelectorAll('[data-index="option2"]');
            const optionSelector3 = target.querySelectorAll('[data-index="option3"]');
            let is_swatch_selector1 = false;
            let is_swatch_selector2 = false;
            let is_swatch_selector3 = false;

            if (optionSelector1.length > 0 && optionSelector1[0].getAttribute('type') == 'radio') {
                is_swatch_selector1 = true;
            }

            if (optionSelector2.length && optionSelector2[0].getAttribute('type') == 'radio') {
                is_swatch_selector2 = true;
            }

            if (optionSelector3.length && optionSelector3[0].getAttribute('type') == 'radio') {
                is_swatch_selector3 = true;
            }

            const option1 = target.querySelectorAll('input[data-index="option1"]');
            const option2 = target.querySelectorAll('input[data-index="option2"]');
            const option3 = target.querySelectorAll('input[data-index="option3"]');

            // If there are 3 option selectors
            if (optionSelector3.length > 0) {

                // If the third option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 2.
                if (is_swatch_selector3 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option3.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option3.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 3.
                } else if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2 and 3.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }

            // If there are 2 option selectors
            } else if (optionSelector2.length > 0) {

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1.
                if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            } else {
                if (is_swatch_selector1 == true) {
                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            }
        },

        initStickyCart() {
            const options = {
                rootMargin: '100px 0px 0px 0px',
                threshold: [0,0.01,1]
            };
            const target = document.querySelector('#productStickyContainer');
            const observer = new IntersectionObserver(this.showHideStickyCart, options);

            observer.observe(target);
        },

        showHideStickyCart(entries, observer) {
            let entry = entries[0];
            if (entry.intersectionRatio >= 1) {
                entry.target.classList.remove('product__sticky--show');
            } else if (entry.intersectionRatio <= 0 && entry.boundingClientRect.y <= 0) {
                entry.target.classList.add('product__sticky--show');
                entry.target.classList.remove('product__sticky--hide');
            } else {
                entry.target.classList.add('product__sticky--hide');
            }
        },

        initProductVideo: function() {
            const sectionId = this.id;
            const videoContainers = document.querySelectorAll(selectors.productMediaTypeVideo, this.container);

            videoContainers.forEach((videoContainer) => {
                ProductVideo.init(videoContainer, sectionId);
            });
        },

        initProductZoom: function() {
            if (matchMedia('(pointer:fine)').matches && window.innerWidth> 1023) {
                const driftImgs = document.querySelectorAll('.js--image-zoom');
                const pane = document.querySelector('.product__feature-image');

                driftImgs.forEach((image) => {

                    // Documentation link for reference: https://github.com/imgix/drift#basic-usage
                    new Drift(image, {
                        paneContainer: pane,
                        inlinePane: false,
                        handleTouch: false,
                        injectBaseStyles: true,
                        hoverDelay: 200,
                    });
                });
            }
        },

        initProductCarousel() {
            let self = this;
            // -------------------------------------
            // Carousel Products
            // -------------------------------------
            const productFeatureSwiper = new Swiper('.'+selectors.productFeatureSlider+'', {
                // Optional parameters
                modules: [Navigation, Pagination],
                spaceBetween: 0,
                pagination: {
                    el: '.swiper-pagination',
                    type: 'bullets',
                    clickable: 'true',
                    bulletElement: 'button'
                },
                on: {
                    init: function() {
                        toggleInnerFocus(this);
                    }
                }
            });

            // The "on" event is proper to the Swiper JS library.
            productFeatureSwiper.on('slideChange ', function () {
                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const previousSlideIndex = productFeatureSwiper.previousIndex,
                    currentSlideIndex = productFeatureSwiper.activeIndex;

                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const currentMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+currentSlideIndex+'"] .product-single__media-wrapper'),
                    previousMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+previousSlideIndex+'"] .product-single__media-wrapper');

                previousMedia.dispatchEvent(new Event('mediaHidden'));
                currentMedia.dispatchEvent(new Event('mediaVisible'));
                toggleInnerFocus(this);
            });

            selectors.productSwiperFeature = productFeatureSwiper;
        },

        slideToSlide(slider, slideIndex, transition) {
            if (transition) {
                slider.slideTo(slideIndex);
            } else {
                slider.slideTo(slideIndex, 0);
            }
        },

        // triggering change image when using with Slider
        swapImage(imageId) {
            const newImageId = imageId;

            // If "imageId" is not defined in the function parameter, find it by the current product image
            if (typeof newImageId === 'undefined') {
                newImageId = document.querySelector(
                    `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`
                ).dataset.imageId;
            }

            const thumbnail = document.querySelector(`.carousel--featured-product .product-single__media-wrapper[data-media-id="${newImageId}"]`);
            const slideIndex = parseInt(thumbnail.parentElement.getAttribute('data-index'));

            // Swipe JS
            this.slideToSlide(selectors.productSwiperFeature, slideIndex, false);
        },

        /**
         * Event callback for Theme Editor `section:unload` event
         */
        onUnload() {
            this.productForm.destroy();
        },

    });



    /**
     * Load Video content on Product Swiper
     */
    const ProductVideo = (() => {
        const videos = {};

        const hosts = {
            html5: 'html5',
            youtube: 'youtube'
        };

        const attributes = {
            enableVideoLooping: 'enable-video-looping',
            videoId: 'video-id'
        };

        // Initialize Video Container
        const init = (videoContainer, sectionId) => {
            if (!videoContainer) {
                return;
            }

            const videoElement = videoContainer.querySelectorAll('iframe, video')[0];
            const mediaId = videoContainer.dataset.mediaId;

            if (!videoElement) {
                return;
            }

            videos[mediaId] = {
                mediaId: mediaId,
                sectionId: sectionId,
                host: hostFromVideoElement(videoElement),
                container: videoContainer,
                element: videoElement,
            };

            const video = videos[mediaId];
            switch (video.host) {
                case hosts.html5:
                    setupPlyrVideos();
                    break;
                case hosts.youtube:
                    setupYouTubeVideos();
                    break;
            }
        };

        // Set Up HTML 5 Players
        const setupPlyrVideos = () => {
            loadVideos(hosts.html5);
        };

        // Set Youtube embedded Videos
        const setupYouTubeVideos = () => {
            window.YT.ready(function() {
                loadVideos(hosts.youtube);
            });
        };

        // Create Video Player
        const createPlayer = (video) => {
            if (video?.player) {
                return;
            }

            const productMediaWrapper = video.container.closest(
                selectors.productMediaWrapper
            );
            const enableLooping = productMediaWrapper.dataset[attributes.enableVideoLooping];

            switch (video.host) {
                case hosts.html5:
                    video.player = new Shopify.Plyr(video.element, {
                        loop: { active: enableLooping }
                    });
                    toggleInnerFocus(selectors.productSwiperFeature);
                    break;
                case hosts.youtube:
                    const videoId = productMediaWrapper.dataset[attributes.videoId];
                    video.player = new YT.Player(video.element, {
                        videoId: videoId,
                        events: {
                            onStateChange: (event) => {
                                if (event.data === 0 && enableLooping) event.target.seekTo(0);
                            }
                        }
                    });
                    break;
            }

            productMediaWrapper.addEventListener('mediaHidden', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.pause();
                }

                if (video.host === hosts.youtube && video.player.pauseVideo) {
                    video.player.pauseVideo();
                }
            });

            productMediaWrapper.addEventListener('mediaVisible', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.play();
                }

                if (video.host === hosts.youtube && video.player.playVideo) {
                    video.player.playVideo();
                }
            });
        };

        // Get host source from Video
        const hostFromVideoElement = (video) => {
            if (video.tagName === 'VIDEO') {
                return hosts.html5;
            }

            if (video.tagName === 'IFRAME') {
                if (
                    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.?be)\/.+$/.test(
                        video.src
                    )
                ) {
                    return hosts.youtube;
                }
            }
            return null;
        };

        // Load Video Players
        const loadVideos = (host) => {
            for (const videoId in videos) {
                const video = videos.hasOwnProperty(videoId) ? videos[videoId] : null;

                if (video?.host === host) createPlayer(video);
            }
        };

        // Remove Video Players
        const removeSectionVideos = (sectionId) => {
            for (const videoId in videos) {
                const video = videos.hasOwnProperty(videoId) ? videos[videoId] : null;

                if (video?.sectionId === sectionId) {
                    if (video.player) video.player.destroy();
                    delete videos[key];
                }
            }
        };

        return {
            init: init,
            hosts: hosts,
            loadVideos: loadVideos,
            removeSectionVideos: removeSectionVideos
        };
    })();

    load('product');
});

// ---------------------------
// Slider Accessibility Helper
// ---------------------------
const toggleInnerFocus = (slider) => {
    const focusableElements = ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer'];
    focusableElements.forEach((fosusableElement) => {
        slider.slides.forEach((slide, index) => {
            if (index === slider.activeIndex) {
                if (fosusableElement === '.plyr') {
                    slide.querySelector(fosusableElement)?.setAttribute('tabindex', '0');
                } else {
                    slide.querySelectorAll(fosusableElement).forEach((elem) => {
                        elem?.removeAttribute('tabindex');
                    });
                }
            } else {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    elem?.setAttribute('tabindex', '-1');
                });
            }
        });
    });
};
